import { CurrencyMap, CustomerServiceProvider, Country } from '@/common/config';
import Api from '@/common/api';
import * as PleasedApi from '@/common/api/api.pleased';
import * as Locale from '@/plugins/i18n';
import * as env from '@/common/environment';
import { platform } from '@/common/bridge';
import { InitializeOptions, LoginResult, PleasedGuestUserInfo, PleasedUserInfo, SelectedEntity } from '@/common/types';
import { ActionContext } from 'vuex';
import { reportingService } from '@/common/reporting';
import { OpenHelpEntryType } from '@/common/entryLog';
import { analytics } from '@/common/logs/EventLogger';
import { capitalizeFirstLetters, isEmailValid, isPhoneNumber } from '@/common/utils';
import { UserInformation } from '@/common/api/models.pleased';
import { CRITICAL_EVENTS } from '@/common/logs';
import { parseErrorMessage } from '@/common/error';

export interface GuestFormData {
  name: string;
  email: string;
  phone: string;
  description: string;
}

export interface UserState {
  isLogin: boolean;
  country: Country | string;
  userCountry: string;
  token?: string;
  chatStatus?: string; // null, chat-bot-start,  live-chat
  avatarImgUrl: string;
  phone: string;
  isEmailVerified: boolean;
  provider: CustomerServiceProvider | '',
  directLink: boolean;
  customerAppVersion: string | null;
  supportVoice: boolean;
  // We have a case of login after being guest (with need to know guest data)
  // That's why now we store both
  pleasedUser: PleasedUserInfo;
  guestUser: PleasedGuestUserInfo;
  guestFormData: GuestFormData | null;
  hideLoginButton: boolean;
  name: string;
  /**
   * Email provided from init.
   */
  email: string;
  metaTags: Record<string, string | number | boolean>;
  /**
   * Transaction currency.
   */
  userCurrency: string;
  usernameFieldType: 'phone' | 'email';
  initSelectedEntity?: SelectedEntity | null;

  sportyLoginPromise: Promise<unknown> | null;
  greyListed?: boolean;

  pageSource?: OpenHelpEntryType;
  sentEntryLog: boolean;
}

interface InitOption {
  token: string,
  country: Country, avatarImgUrl: string, chatStatus: string, phone: string, provider: CustomerServiceProvider, directLink: boolean | null, appVersion: string | null, supportVoice: boolean | null
}

const state: UserState = {
  isLogin: false,
  country: '',
  token: '',
  chatStatus: '',
  avatarImgUrl: '',
  phone: '',
  isEmailVerified: false,
  provider: CustomerServiceProvider.PLEASED,
  directLink: false,
  customerAppVersion: null,
  supportVoice: true,
  guestUser: {
    token: '',
    userId: '',
  },
  guestFormData: null,
  pleasedUser: {
    id: '',
    userId: '',
    foreignId: '',
    email: '',
    phone: '',
    token: '',
    name: '',
    displayName: '',
    defaultName: '',
    greyListed: false,
    isGuest: true,
    userLoyaltyTierBand: '',
    initForeignId: '',
  },
  name: '',
  email: '',
  metaTags: {},
  userCurrency: '',
  userCountry: '',
  usernameFieldType: 'phone',
  sportyLoginPromise: null,
  hideLoginButton: false,
  greyListed: false,
  initSelectedEntity: null,

  sentEntryLog: false,
};

// Check that type/entity is present, including check for fields of entity
// 28.11.2025 Nik – Android version sending empty object for selectedEntity, which resulted in an incident.
function isValidSelectedEntity(selectedEntity?: SelectedEntity | null): boolean {
  if (!selectedEntity) return true;
  return Boolean(selectedEntity && selectedEntity.type && selectedEntity.entity && Object.keys(selectedEntity.entity).length > 0);
}

const getters = {
  maskPhone (state: UserState) { // user phone 777778888711 -> 77*******711
    if (!state.phone) return '***';
    const length = state.phone.length; // TODO:BUG
    const noMask = [0, 1, length - 1, length - 2, length - 3];
    return state.phone && state.phone.split('').reduce((acc, cur, i) => {
      return noMask.includes(i) ? `${acc}${cur}` : `${acc}*`;
    }, '');
  },
  currency (state: UserState) {
    return state.country ? state.userCurrency || CurrencyMap[state.country] : '';
  },
  maskEmail (state: UserState) {
    if (!state.email) return '***';
    return state.email.replace(/^(.)(.*)(.@.*)$/,
      (_, a, b, c) => a + b.replace(/./g, '*') + c
    );
  },
  maskName (state: UserState, getters: Record<string, unknown>) {
    if (state.phone) return getters.maskPhone;
    if (state.email) return getters.maskEmail;
    return '***';
  },
  displayName (state: UserState) {
    return state.name || state.phone || state.email;
  },
  // Should be used instead of state.pleasedUser.name since it will return a capitalized name.
  pleasedUserName (state: UserState) {
    const nameIsEmail = isEmailValid(state.pleasedUser.name);
    const nameIsPhone = isPhoneNumber(state.pleasedUser.name);

    const isNameProvided = state.pleasedUser.name && !nameIsEmail && !nameIsPhone;
    const name = isNameProvided ? capitalizeFirstLetters(state.pleasedUser.name) : '';

    return name;
  },
  // Should be used when pleased name + fallback name is needed.
  pleasedUserDisplayName (_: UserState, getters: Record<string, unknown>) {
    const isNameProvided = !!getters.pleasedUserName;

    const { userName: fallbackName } = env.get('default') || { userName: '' };
    const name = isNameProvided ? getters.pleasedUserName : fallbackName;

    return name;
  },
};

const mutations = {
  deleteChatStatus (state: UserState) {
    state.chatStatus = '';
  },
  // For parents Event
  // INIT
  initProject (state: UserState, payload: InitializeOptions) {
    try {
      console.log('initProject', payload);
      analytics.trackSessionStart({
        sessionParams: {
          language: Locale.i18n.locale,
          platform,
          userAgent: navigator.userAgent,
        },
        metadata: {
          brandCode: payload.brandCode || env.get('brandCode'),
          language: Locale.i18n.locale,
        },
      });

      const rawCountry = payload.sporty?.country || payload.country;
      // Move INT to BR as hotfix since INT moved to BR.
      const country = rawCountry?.toLowerCase() === 'int' ? 'br' : rawCountry;
      Api.setCountry(country as Country);
      state.provider = payload.provider;
      Api.setProvider(payload.provider);

      state.hideLoginButton = !!payload.hideLoginButton;

      if (isValidSelectedEntity(payload.selectedEntity)) {
        state.initSelectedEntity = payload.selectedEntity;
      }

      state.country = country;
      state.pageSource = payload.pageSource;
      state.pleasedUser.initForeignId = payload.userId;
      // state.chatStatus = payload.chatStatus; // currently, Pleased cannot keep the conversation sessions
      state.directLink = !!payload.directLink;
      if (payload.appVersion) {
        state.customerAppVersion = payload.appVersion;
      }
      // android should do the implementation below:
      // 1. AndroidManifest.xml for microphone permission
      // 2, android.webkit.resource.AUDIO_CAPTURE // webrtc request permission
      // 3. webSettings.setMediaPlaybackRequiresUserGesture(false); // without this, apk can only send sound but cannot hear the sound
      if (platform === 'app') {
        state.supportVoice = !!payload.supportVoice;
      } else if (platform === 'ios') { // ios webview doesn't support webrtc
        state.supportVoice = false;
      }

      // Resolve the configuration by country.
      env.setup(country, payload);
      PleasedApi.setConfig({
      // Prefer client configuration.
        url: payload.pleasedUrl || env.get('url'),
        brandCode: payload.brandCode || env.get('brandCode'),
      });

      const token = payload.sporty?.token || payload.token;
      state.userCurrency = payload.sporty?.currency || '';

      const rawUserCountry = payload.sporty?.userCountry || '';
      // Move INT to BR as hotfix since INT moved to BR.
      state.userCountry = rawUserCountry?.toLowerCase() === 'int' ? 'br' : rawUserCountry;

      if (token) {
        Api.setTokenToHeader(token); // Call before set token in case race condition
        state.token = token;
        state.isLogin = true;
        state.phone = payload.phone || '';
        state.email = payload.email || '';
        state.name = payload.name || '';
      }

      state.metaTags = { ...state.metaTags, ...payload.metaTags };

      state.usernameFieldType = payload.login?.usernameType || env.get('userFieldType')?.[country as Country] || 'phone';

      if (payload.defaultLanguage && Locale.isSupported(payload.defaultLanguage) && env.isLocaleEnabled(payload.defaultLanguage)) {
        Locale.setLanguage(payload.defaultLanguage === 'es-mx' as any ? 'es-MX' : payload.defaultLanguage);
      }
    } catch (e) {
      console.error('Error initializing:', e);
      analytics.trackCriticalEvent({
        name: CRITICAL_EVENTS.CHATBOT_INIT_ERROR,
        metadata: {
          error: parseErrorMessage(e),
          payload,
        },
      });
    }
  },
  updatePleasedUser (state: UserState, payload: UserInformation & { isGuest?: boolean }) {
    const { id, token, phone, name = '', email = '', defaultName, isGuest = false, userLoyaltyTierBand = '', ...rest } = payload;

    const displayName = name.trim() || phone || defaultName || '';
    const userId = id;

    state.pleasedUser = {
      id,
      userId,
      token,
      phone,
      name: name.trim(),
      email,
      isGuest,
      displayName,
      defaultName,
      userLoyaltyTierBand,
      initForeignId: state.pleasedUser.initForeignId,
      ...rest,
    };

    PleasedApi.setConfig({ token });
    // Update session with customer id, which will be automatically got from store.
    analytics.updateUserSession({});
  },

  updateGuestUser (state: UserState, payload: UserInformation & { filledDetails?: boolean }) {
    const { id, token, filledDetails = true } = payload;

    state.guestUser = {
      userId: id,
      token,
      filledDetails,
    };
  },

  updateUserGreyListed (state: UserState, payload: { greyListed: boolean }) {
    state.greyListed = payload?.greyListed;
  },
  // LOGIN, LOGIN_POP
  updateLoginStatus (state: UserState, payload: LoginResult) {
    if (payload.token) {
      state.token = payload.token;
      state.isLogin = true;
      state.phone = payload.phone || '';
      state.email = payload.email || '';
      state.name = payload.name || '';
      Api.setTokenToHeader(payload.token);
    }
    state.metaTags = { ...state.metaTags, ...payload.metaTags };
    if (state.avatarImgUrl) {
      state.avatarImgUrl = payload.avatarImgUrl || '';
    }
  },
  updateAvatarImgUrl (state: UserState, url: string) {
    if (url) { state.avatarImgUrl = url; }
  },
  updateEmailVeryfied (state: UserState, bool: boolean) {
    state.isEmailVerified = bool;
  },
  updateSentEntryLog (state: UserState, bool: boolean) {
    state.sentEntryLog = bool;
  },
  updatePageSource (state: UserState, pageSource: OpenHelpEntryType) {
    state.pageSource = pageSource;
  },
  setSportyPromise (state: UserState, promise: Promise<unknown> | null) {
    state.sportyLoginPromise = promise;
  },

  setUserPhoneOrEmailIfNotExists (state: UserState, payload: Partial<Pick<UserState, 'phone' | 'email' | 'name'>>) {
    if (payload.phone && !(state.phone || '').trim()) state.phone = `${payload.phone}`.trim();
    if (payload.email && !(state.email || '').trim()) state.email = `${payload.email}`.trim();
    if (payload.name && !(state.name || '').trim()) state.name = `${payload.name}`.trim();
  },

  updateSelectedEntity (state: UserState, payload: SelectedEntity | null) {
    if (isValidSelectedEntity(payload)) {
      state.initSelectedEntity = payload;
    }
  },

  // Guest Form Data mutations
  setGuestFormData (state: UserState, payload: GuestFormData) {
    state.guestFormData = payload;
  },
  clearGuestFormData (state: UserState) {
    state.guestFormData = null;
  },
};

const actions = {
  async sportyLoginSuccess ({ state, commit }: ActionContext<UserState, unknown>, payload: LoginResult) {
    console.log('sportyLoginSuccess', {
      payload,
      state,
      sportyLoginPromise: state.sportyLoginPromise,
      token: state.token,
      payloadToken: payload.token,
      pleasedUserToken: state.pleasedUser.token,
    });

    if (state.sportyLoginPromise) {
      await state.sportyLoginPromise;
    }

    const hasPleasedUser = !!state?.pleasedUser?.token && !state?.pleasedUser?.isGuest;

    if (payload.token !== state.token || !hasPleasedUser) {
      commit('updateLoginStatus', payload);
      const promise = PleasedApi.getPleasedTokenFromSportyToken({ countryCode: state.country, sportyToken: payload.token, userCountry: payload.userCountry });
      commit('setSportyPromise', promise.then(() => { commit('setSportyPromise', null); }));
      const res = await promise;
      console.log('sporty login res', res);
      reportingService.userLoggedIn();
      commit('updatePleasedUser', res);
      commit('updateUserGreyListed', {
        greyListed: !!res.greyListed,
      });
    } else { console.warn('[User:sportyLoginSuccess]: Token already registered'); }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
